import { useState } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { Text, TextBlock } from '../../Atoms/Text';
import { Comment, archiveComment, updateComment } from '../../../store/Comment';
import { getFormattedDate } from '../../../utils/helpers';
import { getCurrentUser, isAdmin } from '../../../store/User';
import { TextEditor } from './TextEditor';
import { User } from '../../../store/User';

export interface CommentItemProps {
  comment: Comment;
  handleReply: (comment: Comment) => void;
  mentionableUsers: User[];
}

export function CommentItem({ comment, handleReply, mentionableUsers }: CommentItemProps) {
  const [showMore, setShowMore] = useState<boolean>(false);
  const [isArchiving, setIsArchiving] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const user = getCurrentUser();
  const isOwner = String(comment.createdBy?.id) == user?.id;

  const isArchivable = () => {
    return !comment.archived && (isOwner || isAdmin());
  };

  const handleArchive = () => {
    setIsArchiving(true);
    setIsEditing(false);
    archiveComment(comment);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleUpdateComment = (commentText: string) => {
    updateComment({ ...comment, commentText: commentText || '' });
    setIsEditing(false);
  };
  return (
    <div
      id={comment.id}
      className={`mb-3 last:pb-5 ${comment.parent_comment_id ? 'ml-6' : 'pt-3 border-t border-grey-2 first:border-t-0'}`}>
      <div className="flex gap-2">
        {!!comment.createdBy?.firstName && (
          <TextBlock
            color="grey9"
            size="sm"
            weight="medium">
            {comment.createdBy?.firstName || ''} {comment.createdBy?.lastName || ''}
          </TextBlock>
        )}
        {comment.createdAt && (
          <TextBlock
            size="sm"
            color="grey6">
            {getFormattedDate(comment.createdAt, 'lll')}
          </TextBlock>
        )}
        {comment.textChangedAt && (
          <TextBlock
            className="italic"
            size="sm"
            color="grey6">
            (edited)
          </TextBlock>
        )}
      </div>
      <div className={isEditing ? 'mt-4' : ''}>
        {!isEditing && (
          <div className={`text-grey-9 text-sm ql-editor pl-0 ${comment.archived ? 'italic' : ''}`}>
            {parse(
              DOMPurify.sanitize(
                (comment.commentText || '').length > 500 && !showMore
                  ? `${comment.commentText.substring(0, 500)}<span>...</span>`
                  : comment.commentText || ''
              )
            )}
          </div>
        )}
        {isEditing && (
          <TextEditor
            commentText={comment.commentText}
            handleCancel={handleCancelEdit}
            handleSend={handleUpdateComment}
            portalUsers={mentionableUsers}
            placeholder="Add a comment"
          />
        )}
      </div>
      <div className="flex gap-4 mt-1">
        {!isEditing && (comment.commentText || '').length > 500 && (
          <button onClick={() => setShowMore(!showMore)}>
            <Text
              size="sm"
              color="indigo">
              {showMore ? 'Show Less' : 'Show More'}
            </Text>
          </button>
        )}
        <button onClick={() => handleReply(comment)}>
          <Text
            size="sm"
            color="indigo">
            Reply
          </Text>
        </button>
        {!comment.archived && isOwner && (
          <button
            disabled={isArchiving}
            onClick={() => handleEdit()}>
            <Text
              color="indigo"
              size="sm">
              Edit
            </Text>
          </button>
        )}
        {isArchivable() && (
          <button
            disabled={isArchiving}
            onClick={() => handleArchive()}>
            <Text
              size="sm"
              color="indigo">
              Delete
            </Text>
          </button>
        )}
      </div>
    </div>
  );
}
